import { useEffect, useState } from 'react';
import { Slate } from 'components/Slate';
import { createSessionID, formatNumberAsCurrency, getUrlParams, scrollToTop } from 'helpers/utils';
import logFirebaseEvent from 'helpers/logFirebaseEvent';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getRecipientsState } from 'selectors/recipients';
import {
  clearAccessToken,
  clearDonationSelections,
  getSessionID,
  setSessionID,
} from 'services/storage';
import { getRecipients } from 'store/recipients/actions';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import { useIsOathCheckoutEnabled, useOathCheckout } from 'hooks/donate';
import {
  democracyEngineCheckoutRedirect,
  engageLabsCheckoutRedirect,
  getCandidates,
  getCandidatesByImpactScore,
  getCandidatesWithNewAmounts,
  getDonationSelectionsObj,
  getProcessingRedirect,
  getSelectedCandidates,
  logFirebaseEventDonateIntent,
  validateDonation,
} from 'helpers/donate';
import { getUserState } from 'selectors/user';
import { getUserProfile } from 'store/user/actions';
import { useAuth } from 'hooks/auth';
import { getDonateConfig } from 'selectors/siteConfig';
import { IPage, IPageType, ProcessingRedirect } from 'store/pages/types';
import { ProcessingRedirectOptions } from 'store/pages/constants';
import { getAllPages, getPagesState } from 'selectors/pages';
import { getPages } from 'store/pages/actions';
import { getFilters } from 'store/filters/actions';
import { EventSlate } from 'components/Slate/EventSlate';
import { IEvent } from 'store/event/types';
import { getEventData, getEventState } from 'selectors/event';
import { getEvent } from 'store/event/actions';
import { getPayDafData } from 'selectors/pay';
import { Route, Routes, useNavigate } from 'react-router-dom';
import HeaderAndFooterLayout from 'layouts/HeaderAndFooter';
import DonateAndCheckoutLayout from 'layouts/DonateAndCheckout';
import { Confirmation } from 'screens/Confirmation';
import { CheckoutBase } from 'screens/CheckoutBase';
import { ENV_CONFIG } from 'config/environment';
import { showToast } from 'components/Toast';
import { getChariotConnectId } from 'components/Slate/utils';

const MAX_RECIPIENTS = 200;
const MIN_RECIPIENTS = 6;

export const MainScreen = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [refWidthValue, setRefWidthValue] = useState(0);
  const [pageData, setPageData] = useState<IPage[] | IEvent[]>([]);
  const { urlTags, urlRef, urlPartner, urlEvent } = getUrlParams();
  const [processingRedirect, setProcessingRedirect] = useState<ProcessingRedirect>(
    ProcessingRedirectOptions.DE_OATH
  );
  const [pageType, setPageType] = useState<IPageType | undefined>();
  const isOathCheckoutEnabled = useIsOathCheckoutEnabled();
  const {
    recipients,
    pagesState,
    allPages,
    userState,
    donatModalConfig,
    eventState,
    eventData,
    dafData,
  } = useAppSelector(state => ({
    recipients: getRecipientsState(state),
    pagesState: getPagesState(state),
    allPages: getAllPages(state),
    userState: getUserState(state),
    donatModalConfig: getDonateConfig(state, 'donate_modal'),
    eventState: getEventState(state),
    eventData: getEventData(state),
    dafData: getPayDafData(state),
  }));
  const [selectedDonationAmount, setSelectedDonationAmount] = useState<number | undefined>();
  const [candidates, setCandidates] = useState<CandidateType[]>([]);
  const [showCustomSplit, setShowCustomSplit] = useState(false);
  const oathCheckout = useOathCheckout();

  useAuth(() => dispatch(getUserProfile()));

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (userState.error) clearAccessToken();
  }, [userState]);

  useEffect(() => {
    if (urlEvent) {
      dispatch(getEvent({ urlPartner }));
    } else if (!allPages) {
      if (urlPartner) dispatch(getPages({ urlPartner }));
      dispatch(getFilters());
    }
    if (!recipients.data && !urlPartner) {
      dispatch(
        getRecipients({
          amount: urlPartner ? MAX_RECIPIENTS : MIN_RECIPIENTS,
          is_public_searchable: true,
        })
      );
    }
    if (!getSessionID()) {
      setSessionID(createSessionID(10));
    }
    logFirebaseEvent('land_visitor', {
      timestamp: new Date().toString(),
      ref: urlRef,
      tags: urlTags,
      partner: urlPartner,
      session_id: getSessionID(),
    });
  }, []);

  useEffect(() => {
    if (urlEvent && eventData) {
      setPageData([eventData]);
    } else if (!urlEvent && allPages?.length && urlPartner) {
      setPageData(allPages);
    } else if (!urlPartner) {
      // no page data for search page
      setPageData([]);
    }
  }, [allPages, eventData]);

  useEffect(() => {
    if (pageData?.length > 0) {
      if (!recipients.data && urlPartner) {
        dispatch(
          getRecipients({
            amount: MAX_RECIPIENTS,
            recipient_ids: pageData[0]?.recipient_ids || [],
          })
        );
      }
      const updatedProcessingRedirect = getProcessingRedirect(pageData);
      const updatedPageType =
        pageData?.findIndex(page => page.page_type === 'LIST') >= 0
          ? 'LIST'
          : pageData[0].page_type;
      setProcessingRedirect(updatedProcessingRedirect);
      setPageType(updatedPageType);
    }
  }, [pageData]);

  useEffect(() => {
    const { candidates: savedCandidates, total: savedDonationAmount } = getDonationSelectionsObj();
    if (savedDonationAmount) {
      setSelectedDonationAmount(Number(savedDonationAmount));
    }
    if (recipients?.data?.recipients && !urlPartner && !savedCandidates) {
      setCandidates(getCandidatesByImpactScore(recipients.data.recipients));
    } else if (recipients?.data?.recipients && !candidates.length) {
      setCandidates(getCandidates(recipients.data.recipients, savedCandidates));
    }
  }, [recipients?.data]);

  useEffect(() => {
    if (selectedDonationAmount && candidates.length) {
      clearDonationSelections();
    }
  }, [selectedDonationAmount, candidates]);

  const onCustomSplit = (isCustomSplit: boolean) => {
    setShowCustomSplit(isCustomSplit);
  };

  const onUserDonationTotalChange = (amount?: number) => {
    setSelectedDonationAmount(amount);
    setCandidates(getCandidatesWithNewAmounts(amount, candidates, onCustomSplit));
  };

  const onSelectCandidates = (updatedCandidates: CandidateType[]) => {
    setCandidates(
      getCandidatesWithNewAmounts(selectedDonationAmount, updatedCandidates, onCustomSplit)
    );
  };

  const handleCandidatesSelected = (index: number, checkValue: boolean) => {
    const updatedCandidates = [...candidates];
    updatedCandidates[index] = {
      ...candidates[index],
      isSelected: checkValue,
    };
    onSelectCandidates(updatedCandidates);
    logFirebaseEvent(checkValue ? 'land_select_recipient' : 'land_unselect_recipient', {
      timestamp: new Date().toString(),
      recipient_id: candidates[index].recipient_id,
      session_id: getSessionID(),
    });
  };

  const onDafDonateClick = () => {
    if (!selectedDonationAmount || selectedDonationAmount < 1)
      showToast(`Donation amount must be greater than ${formatNumberAsCurrency(1)}`, 'error');
    else navigate(`/donate/checkout/daf?p=${urlPartner}`);
  };

  const onDonateClick = (selectedFilters?: string[]) => {
    const selectedCandidates = getSelectedCandidates(candidates).filter(
      candidate => candidate.donationAmount && candidate.donationAmount > 0
    );
    const isDonationValid = validateDonation(selectedCandidates, selectedDonationAmount);
    if (!isDonationValid || !selectedDonationAmount) {
      return;
    }
    logFirebaseEventDonateIntent(selectedDonationAmount, candidates, selectedFilters);
    if (pageData[0]?.processing_redirect === ProcessingRedirectOptions.STRIPE) {
      navigate(`/donate/checkout/standard?p=${urlPartner}`);
    } else if (isOathCheckoutEnabled) {
      oathCheckout(selectedCandidates, selectedDonationAmount, selectedFilters);
    } else if (
      processingRedirect === ProcessingRedirectOptions.DE_GENERAL ||
      processingRedirect === ProcessingRedirectOptions.DE_OATH
    ) {
      const queryStrList = pageData
        ?.filter(page => selectedFilters?.includes(page.value))
        ?.map(page => page.query_str);
      democracyEngineCheckoutRedirect(selectedCandidates, selectedDonationAmount, '', queryStrList);
    } else if (processingRedirect === ProcessingRedirectOptions.EN_GENERAL) {
      engageLabsCheckoutRedirect(selectedCandidates, selectedDonationAmount);
    }
  };

  const isMissingPages = !!urlPartner && !pageData?.length;
  const hasApiError = pagesState.error || recipients.error || eventState.error;
  const isLoadingPages = !!urlPartner && (pagesState.isLoading || eventState.isLoading);

  const headerVisible =
    !urlPartner || !pageData?.length || (pageData?.length > 0 && !pageData[0].whitelabeled);

  useEffect(() => {
    if (dafData) {
      navigate('/donate/confirmation/daf');
    }
  }, [dafData]);

  return (
    <HeaderAndFooterLayout
      footerVisible={true}
      headerVisible={headerVisible}
      isLoading={!hasApiError && isLoadingPages}
    >
      <Routes>
        <Route
          path="/"
          element={
            <DonateAndCheckoutLayout
              error={
                hasApiError
                  ? 'An error has occured. Contact us.'
                  : isMissingPages
                  ? 'Donate page data not found.'
                  : undefined
              }
              leftSideSetRefWidthValue={setRefWidthValue}
              showPausedModal={pageData[0]?.status === 'PAUSED'}
              showLeftSide={pageType !== 'MEDIA'}
              showDonateModal={
                pageData[0]?.status === 'ACTIVE' &&
                pageData[0]?.owner === 'OATH' &&
                donatModalConfig?.donate_modal_config === 'on' &&
                donatModalConfig?.donate_modal_link &&
                !donatModalConfig?.donate_modal_link?.includes(`p=${urlPartner}`)
              }
              pausedMisc={pageData[0]?.paused_misc}
            />
          }
        >
          <Route
            index
            element={
              <div>
                {urlEvent ? (
                  <EventSlate
                    candidates={candidates}
                    eventData={eventData}
                    handleCandidatesSelected={handleCandidatesSelected}
                    onDonateClick={onDonateClick}
                    onSelectCandidates={onSelectCandidates}
                    onUserDonationTotalChange={onUserDonationTotalChange}
                    selectedDonationAmount={selectedDonationAmount}
                    setSelectedDonationAmount={setSelectedDonationAmount}
                    type={pageType || 'LIST'}
                  />
                ) : (
                  <Slate
                    allPages={pageData}
                    recipients={recipients?.data?.recipients}
                    candidates={candidates}
                    onSelectCandidates={onSelectCandidates}
                    handleCandidatesSelected={handleCandidatesSelected}
                    selectedDonationAmount={selectedDonationAmount}
                    onDonateClick={onDonateClick}
                    onUserDonationTotalChange={onUserDonationTotalChange}
                    setCandidates={setCandidates}
                    setSelectedDonationAmount={setSelectedDonationAmount}
                    showCustomSplit={showCustomSplit}
                    refWidthValue={refWidthValue}
                    type={pageType || 'LIST'}
                    onDafDonateClick={onDafDonateClick}
                    isDafEnabled={
                      pageData[0]?.subscription_level === 'PREMIUM' &&
                      !!getChariotConnectId(candidates)
                    }
                  />
                )}
              </div>
            }
          />
          <Route
            path="checkout/*"
            element={
              <CheckoutBase
                candidates={candidates}
                clientId={pageData?.[0]?.client_id.toString()}
                donationAmount={selectedDonationAmount || 0}
                pageId={pageData?.[0]?.id}
              />
            }
          />
        </Route>
        <Route
          path="/confirmation/:paymentMethod"
          element={
            <Confirmation
              candidates={candidates}
              donationAmount={selectedDonationAmount || 0}
              isEvent={urlEvent}
              pageImage={
                urlPartner && pageData?.length > 0
                  ? `${ENV_CONFIG().CLOUD_STORAGE.URL}/${ENV_CONFIG().CLOUD_STORAGE.TAGS}/tag_${
                      pageData[0].id
                    }.png`
                  : ''
              }
            />
          }
        />
      </Routes>
    </HeaderAndFooterLayout>
  );
};
